import { useNavigate } from 'react-router-dom';
import { useCallback } from 'react';
import InfiniteScroll from 'react-infinite-scroll-component';
import { Grid, Stack, Typography } from '@mui/material';

import { Icon, Loading } from '@hapvida/hapvida-core-components';

import { NEW_COLORS } from '@hapvida/hapvida-core-components/src/themes/colors/newColors';

import { DependentProps } from '@services/BeneficiaryService';

import { BeneficiaryListSortNameEnum } from '@flows/private/beneficiaries/pages/BeneficiaryList/constants/columns';
import { BeneficiaryDetailRouter } from '@flows/private/beneficiaries/pages/BeneficiaryDetails/router';
import {
  BeneficiaryType,
  Detail,
  DetailContract,
  Person,
  Plan,
} from '@flows/private/beneficiaries/components';

import { DependentComponent } from '../../@types';
import { ActionButton, Line, StyledGrid } from './styles';
import type { BenefificaryListCardMobileProps } from './types';

export function BeneficiaryListCardMobile({
  beneficiariesList,
  hasNextPage,
  fetchNextPage,
}: Readonly<BenefificaryListCardMobileProps>) {
  const navigate = useNavigate();

  const handleEdit = useCallback(
    (id: string) => {
      navigate(
        BeneficiaryDetailRouter.dynamicPath({ beneficiaryId: id } as any),
      );
    },
    [navigate],
  );

  const beneficiaryRow = useCallback(
    (beneficiary: DependentProps) => ({
      detail: <Detail data={beneficiary} />,
      person: <Person data={beneficiary?.user} />,
      [BeneficiaryListSortNameEnum.COMPANY]: (
        <>
          <Typography variant="body2" fontWeight={700}>
            {beneficiary?.healthOperator}
          </Typography>
        </>
      ),
      [BeneficiaryListSortNameEnum.PLAN]: (
        <Plan
          name={beneficiary?.contractDetail?.planHealth?.name}
          code={beneficiary?.contractDetail?.planHealth?.code}
          ansCode={beneficiary?.contractDetail?.planHealth?.ansCode}
        />
      ),
      [BeneficiaryListSortNameEnum.PARTNER_COMPANY]: (
        <DetailContract data={beneficiary?.contractDetail} />
      ),
    }),
    [],
  );

  const beneficiaryData =
    beneficiariesList?.pages
      ?.map(beneficiary => beneficiary.beneficiaries)
      .flat() ?? [];

  const tableComponents = beneficiaryData
    .map(item => {
      let mainRow: Record<string, any> = {};
      const dependents: DependentComponent[] = [];

      if (item?.holder) {
        mainRow = {
          ...beneficiaryRow(item?.holder),
          [BeneficiaryListSortNameEnum.TYPE]: <BeneficiaryType data={item} />,
        };

        if (item?.holder?.dependents && item?.holder?.dependents?.length > 0) {
          item?.holder?.dependents?.forEach((dependent: DependentProps) => {
            const dependentsRow = {
              ...beneficiaryRow(dependent),
              [BeneficiaryListSortNameEnum.TYPE]: (
                <BeneficiaryType dependent={dependent} />
              ),
            };

            const rowDataDependents = {
              id: dependent?.code,
              itHasError: !!dependent?.review,
              errorMessage: dependent?.review?.message ?? '',
              components: dependentsRow,
            };

            dependents.push(rowDataDependents);
          });
        }

        return {
          id: item?.holder?.code,
          itHasError: !!item?.holder?.review,
          errorMessage: item?.holder?.review?.message ?? '',
          main: mainRow,
          dependents,
        };
      }

      if (item?.beneficiary) {
        mainRow = {
          ...beneficiaryRow(item?.beneficiary),
          [BeneficiaryListSortNameEnum.TYPE]: <BeneficiaryType data={item} />,
        };

        return {
          id: item?.beneficiary?.code,
          itHasError: !!item?.beneficiary?.review,
          errorMessage: item?.beneficiary?.review?.message ?? '',
          main: mainRow,
          dependents,
        };
      }

      return null;
    })
    .filter(Boolean);

  return (
    <InfiniteScroll
      dataLength={beneficiaryData?.length}
      hasMore={!!hasNextPage}
      next={fetchNextPage}
      loader={<Loading animationProps={{ height: 20, width: 20 }} />}
      height={'800px'}
    >
      <Grid container gap={1} flexDirection="row" flexWrap="wrap">
        {tableComponents?.map(beneficiary => (
          <Grid item xs={12} key={beneficiary?.main.id}>
            <Line container alignItems="center">
              <Stack
                flexDirection="row"
                alignItems="flex-start"
                mb={2}
                width="100%"
                justifyContent="flex-start"
              >
                <Stack
                  alignItems="flex-start"
                  justifyContent="flex-start"
                  minWidth="50%"
                >
                  <Stack mb={3}>{beneficiary?.main.detail}</Stack>
                  <Stack mb={1.5}>{beneficiary?.main.Type}</Stack>
                  <Stack mb={1.5}>{beneficiary?.main.HealthOperator}</Stack>
                  {beneficiary?.main.CodePlanHealth}
                </Stack>
                <Stack
                  alignItems="flex-start"
                  justifyContent="flex-end"
                  minWidth="50%"
                >
                  <Stack mb={1.5}>{beneficiary?.main.person}</Stack>
                  {beneficiary?.main.PartnerCompany}
                </Stack>
              </Stack>
              <StyledGrid item xs={12}>
                <ActionButton
                  onClick={() => handleEdit(beneficiary?.id ?? '-')}
                  color="secondary"
                  variant="contained"
                  size="small"
                  fullWidth
                  endIcon={
                    <Stack flexDirection="row" alignItems="center" gap={1}>
                      <Typography color="inherit">Mostrar detalhes</Typography>
                      <Icon
                        color={NEW_COLORS.BASE.WHITE}
                        name="actions-b2b-arrow-right"
                        size={24}
                      />
                    </Stack>
                  }
                />
              </StyledGrid>
            </Line>
          </Grid>
        ))}
      </Grid>
    </InfiniteScroll>
  );
}
